import { BarChart } from "@/shared/ui/BarChart";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { PieChart } from "@/shared/ui/PieChart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import type { ExpensesOverviewChartsDTO } from "../api/client";
import {
  EXPENSE_STATUS_LABELS,
  formatExpenseAmount,
} from "../lib/expense-format";

const STATUS_CHART_COLORS: Record<string, string> = {
  draft: "var(--chart-4)",
  submitted: "var(--chart-3)",
  approved: "var(--chart-2)",
  rejected: "var(--destructive)",
  paid: "var(--chart-1)",
  closed: "var(--chart-5)",
  cancelled: "var(--muted-foreground)",
  voided: "var(--destructive)",
};

const CHART_BODY_HEIGHT = "h-[240px]";

function formatMonthLabel(monthKey: string): string {
  try {
    const date = parseISO(`${monthKey}-01`);
    return date.toLocaleDateString("lo-LA", {
      month: "short",
      year: "2-digit",
    });
  } catch {
    return monthKey;
  }
}

function truncateLabel(label: string, max = 14): string {
  if (label.length <= max) return label;
  return `${label.slice(0, max - 1)}…`;
}

type ChartSectionProps = {
  charts: ExpensesOverviewChartsDTO | undefined;
  isLoading: boolean;
};

function ChartLoadingCard({ title }: { title: string }) {
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="space-y-1 border-b px-6 py-4">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent
        className={`flex ${CHART_BODY_HEIGHT} items-center justify-center px-6 py-4`}
      >
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      </CardContent>
    </Card>
  );
}

export function ExpenseOverviewMonthlyChart({
  charts,
  isLoading,
}: ChartSectionProps) {
  const monthData = useMemo(
    () =>
      (charts?.byMonth ?? []).map((row) => ({
        month: formatMonthLabel(row.month),
        amount: row.amount,
      })),
    [charts?.byMonth],
  );

  const monthConfig: ChartConfig = {
    amount: { label: "ຍອດ (ກີບ)", color: "var(--chart-1)" },
  };

  if (isLoading) {
    return <ChartLoadingCard title="ຍອດຕາມເດືອນ" />;
  }

  if (monthData.length === 0) {
    return null;
  }

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="space-y-1 border-b px-6 py-4">
        <CardTitle className="text-base">ຍອດຕາມເດືອນ</CardTitle>
        <CardDescription>6 ເດືອນລ່າສຸດ (ຕາມວັນທີຄ່າໃຊ້ຈ່າຍ)</CardDescription>
      </CardHeader>
      <CardContent className="px-2 pt-4 pb-6">
        <div className={`${CHART_BODY_HEIGHT} w-full`}>
          <BarChart
            data={monthData}
            config={monthConfig}
            xDataKey="month"
            valueDataKey="amount"
            className="h-full"
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function ExpenseOverviewStatusChart({
  charts,
  isLoading,
}: ChartSectionProps) {
  const statusPieData = useMemo(() => {
    return (charts?.byStatus ?? [])
      .filter((row) => row.amount > 0)
      .map((row) => {
        const label = EXPENSE_STATUS_LABELS[row.status] ?? row.status;
        return {
          name: label,
          value: row.amount,
          status: row.status,
        };
      });
  }, [charts?.byStatus]);

  const statusPieConfig = useMemo(() => {
    const config: ChartConfig = {};
    for (const row of statusPieData) {
      config[row.name] = {
        label: row.name,
        color: STATUS_CHART_COLORS[row.status] ?? "var(--chart-1)",
      };
    }
    return config;
  }, [statusPieData]);

  if (isLoading) {
    return <ChartLoadingCard title="ຍອດຕາມສະຖານະ" />;
  }

  if (statusPieData.length === 0) {
    return null;
  }

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="space-y-1 border-b px-6 py-4">
        <CardTitle className="text-base">ຍອດຕາມສະຖານະ</CardTitle>
        <CardDescription>ສັດສ່ວນຍອດລວມຕາມ workflow</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 px-6 py-6">
        <div className="mx-auto h-[200px] w-full max-w-[200px]">
          <PieChart
            data={statusPieData}
            config={statusPieConfig}
            dataKey="value"
            nameKey="name"
            innerRadius={48}
            outerRadius={84}
            className="h-full"
          />
        </div>
        <ul className="w-full max-w-sm space-y-1.5 text-sm">
          {statusPieData.map((row) => (
            <li
              key={row.status}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate text-muted-foreground">{row.name}</span>
              <span className="shrink-0 font-medium text-xs tabular-nums">
                {formatExpenseAmount(row.value as number)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function ExpenseOverviewCategoryChart({
  charts,
  isLoading,
}: ChartSectionProps) {
  const categoryData = useMemo(
    () =>
      (charts?.byCategory ?? []).map((row) => ({
        category: row.categoryName,
        amount: row.amount,
      })),
    [charts?.byCategory],
  );

  const categoryConfig: ChartConfig = {
    amount: { label: "ຍອດ (ກີບ)", color: "var(--chart-2)" },
  };

  if (isLoading) {
    return <ChartLoadingCard title="ຍອດຕາມຫມວດ" />;
  }

  if (categoryData.length === 0) {
    return null;
  }

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="space-y-1 border-b px-6 py-4">
        <CardTitle className="text-base">ຍອດຕາມຫມວດ</CardTitle>
        <CardDescription>8 ຫມວດທີ່ມີຍອດສູງສຸດ</CardDescription>
      </CardHeader>
      <CardContent className="px-2 pt-4 pb-6">
        <div className={`${CHART_BODY_HEIGHT} w-full`}>
          <BarChart
            data={categoryData}
            config={categoryConfig}
            xDataKey="category"
            valueDataKey="amount"
            xAxisTickFormatter={(value) => truncateLabel(String(value))}
            className="h-full"
          />
        </div>
      </CardContent>
    </Card>
  );
}
